import { useTranslation } from 'react-i18next'

import type { CampaignContactRow } from '#/entities/campaign'
import {
  AddToCampaignDialog,
  toEnrollmentContacts,
} from '#/features/campaign-enrol'
import { DataTable } from '#/shared/ui/data-table'
import { SearchInput } from '#/shared/ui/search-input'

import { useCampaignContactRowActions } from '../model/use-campaign-contact-row-actions'
import { useCampaignContacts } from '../model/use-campaign-contacts'
import type { UseCampaignContactsResult } from '../model/use-campaign-contacts'
import { useCampaignPeopleActions } from '../model/use-campaign-people-actions'
import { CONTACT_DIALOGS, useContactDialog } from '../model/use-contact-dialog'
import type { ContactDialog } from '../model/use-contact-dialog'
import type { CampaignContactFilter } from '../model/contact-filters'
import { CampaignAssignDialog } from './campaign-assign-dialog'
import { useCampaignContactColumns } from './campaign-contact-columns'
import { CampaignFilterChips } from './campaign-filter-chips'
import { CampaignSelectionBar } from './campaign-selection-bar'
import { CampaignTasksDialog } from './campaign-tasks-dialog'

interface CampaignContactTabProps {
  campaignId: string
  /** The chip the tab opens on — `?filter=` from a link into one slice. */
  initialFilter?: CampaignContactFilter
}

/** The three bulk-bar dialogs. `opening` remounts whichever one opens, so it
 *  starts from a blank form rather than the last answer. */
function ContactDialogs({
  campaignId,
  dialog,
  opening,
  onClose,
  selectedIds,
  selectedRows,
  people,
  clearSelection,
}: {
  campaignId: string
  dialog: ContactDialog | null
  opening: number
  onClose: () => void
  selectedIds: string[]
  selectedRows: CampaignContactRow[]
  people: ReturnType<typeof useCampaignPeopleActions>
  clearSelection: () => void
}) {
  const onOpenChange = (open: boolean) => !open && onClose()
  return (
    <>
      <AddToCampaignDialog
        key={`add:${opening}`}
        open={dialog === CONTACT_DIALOGS.add}
        onOpenChange={onOpenChange}
        people={{ contacts: toEnrollmentContacts(selectedRows) }}
        count={selectedRows.length}
        excludeCampaignId={campaignId}
        onAdded={clearSelection}
      />
      <CampaignAssignDialog
        key={`assign:${opening}`}
        open={dialog === CONTACT_DIALOGS.assign}
        onOpenChange={onOpenChange}
        count={selectedIds.length}
        busy={people.isAssigning}
        onSubmit={(userId) => people.onAssign(selectedIds, userId)}
      />
      <CampaignTasksDialog
        key={`tasks:${opening}`}
        open={dialog === CONTACT_DIALOGS.tasks}
        onOpenChange={onOpenChange}
        count={selectedIds.length}
        busy={people.isCreatingTasks}
        onSubmit={(input) => people.onCreateTasks(selectedIds, input)}
      />
    </>
  )
}

/** The bulk bar. Its three dialog items go through `openBulk`, which drops
 *  any row a menu had aimed at — the bar acts on the ticks. */
function ContactBulkBar({
  count,
  onClear,
  busy,
  openBulk,
  onOpenInPhone,
  onRemoveFromSequence,
  onExport,
}: {
  count: number
  onClear: () => void
  busy: boolean
  openBulk: (dialog: ContactDialog) => () => void
  onOpenInPhone: () => void
  onRemoveFromSequence: () => void
  onExport: () => void
}) {
  return (
    <CampaignSelectionBar
      count={count}
      onClear={onClear}
      busy={busy}
      handlers={{
        openInPhone: onOpenInPhone,
        removeFromSequence: onRemoveFromSequence,
        addToAnotherSequence: openBulk(CONTACT_DIALOGS.add),
        assignToAgent: openBulk(CONTACT_DIALOGS.assign),
        createTasks: openBulk(CONTACT_DIALOGS.tasks),
        export: onExport,
      }}
    />
  )
}

type ContactSearchProps = Pick<
  UseCampaignContactsResult,
  'search' | 'onSearchChange' | 'chips' | 'filter' | 'onFilterChange'
>

function ContactSearch({
  search,
  onSearchChange,
  chips,
  filter,
  onFilterChange,
}: ContactSearchProps) {
  const { t } = useTranslation()
  return (
    <>
      <div className="flex flex-wrap items-center gap-3">
        <SearchInput
          size="sm"
          clearable
          className="max-sm:flex-1 sm:w-64"
          placeholder={t('campaigns.contact.search')}
          value={search}
          onChange={(event) => onSearchChange(event.target.value)}
        />
      </div>
      <CampaignFilterChips
        chips={chips}
        active={filter}
        onChange={onFilterChange}
      />
    </>
  )
}

export function CampaignContactTab({
  campaignId,
  initialFilter,
}: CampaignContactTabProps) {
  const contacts = useCampaignContacts(campaignId, initialFilter)
  const { rows, selectedIds, clearSelection } = contacts
  const modal = useContactDialog()
  const people = useCampaignPeopleActions(campaignId, () => {
    modal.close()
    clearSelection()
  })
  const row = useCampaignContactRowActions({
    rows,
    selectedIds,
    onOpenRowInPhone: contacts.onOpenRowInPhone,
    onRemoveRow: contacts.onRemoveRow,
    openDialog: modal.open,
    closeDialog: modal.close,
    busy: contacts.isRemoving || people.isAssigning || people.isCreatingTasks,
  })
  const columns = useCampaignContactColumns(row.actions)

  return (
    <div className="flex flex-col gap-4">
      <ContactBulkBar
        count={selectedIds.length}
        onClear={clearSelection}
        busy={row.actions.busy}
        openBulk={row.openBulk}
        onOpenInPhone={contacts.onOpenInPhone}
        onRemoveFromSequence={contacts.onRemoveFromSequence}
        onExport={contacts.onExport}
      />
      <ContactSearch
        search={contacts.search}
        onSearchChange={contacts.onSearchChange}
        chips={contacts.chips}
        filter={contacts.filter}
        onFilterChange={contacts.onFilterChange}
      />
      <DataTable
        storageKey="campaign-contacts-pins"
        className="[&_thead_th]:text-gray-500 [&_thead_tr]:bg-gray-50"
        columns={columns}
        data={rows}
        isLoading={contacts.isLoading}
        selectable
        selectedIds={selectedIds}
        onSelectionChange={contacts.onSelectionChange}
        pagination={contacts.pagination}
        minWidth={1000}
      />
      {/* The money the list is worth, restated under it — the table's own
          footer counts rows, this counts what they are chasing. */}
      {contacts.summary !== '' && (
        <p className="px-1 text-xs text-gray-500">{contacts.summary}</p>
      )}
      <ContactDialogs
        campaignId={campaignId}
        dialog={modal.dialog}
        opening={modal.opening}
        onClose={row.onDialogClose}
        selectedIds={row.actionIds}
        selectedRows={row.actionRows}
        people={people}
        clearSelection={clearSelection}
      />
    </div>
  )
}
